import React, { createContext, useContext, useState, useMemo, useCallback, useRef, Dispatch, SetStateAction } from 'react';

interface DashboardDataContextInterface {
	selectedSources: any[];
	setSelectedSources: Dispatch<SetStateAction<any[]>>;
	selectedParamsList: any[];
	setSelectedParamsList: Dispatch<SetStateAction<any[]>>;
	selectedRowKeys: any[];
	setSelectedRowKeys: Dispatch<SetStateAction<any[]>>;

	sourcesByParam: any[];
	setSourcesByParam: Dispatch<SetStateAction<any[]>>;
    showSources: boolean;
    setShowSources: Dispatch<SetStateAction<boolean>>;
    seriesVisibilityState: Map<string, boolean>;
    setSeriesVisibilityState: Dispatch<SetStateAction<Map<string, boolean>>>;
    toggleSeriesVisibility: (record: any) => void;
    showStrategies: boolean;
    setShowStrategies: Dispatch<SetStateAction<boolean>>;
    selectedSourceToAnnotation: string;
    setSelectedSourceToAnnotation: Dispatch<SetStateAction<string>>;
    selectedQualityAnnotationRange: {start: number, end: number};
    setSelectedQualityAnnotationRange: Dispatch<SetStateAction<{start: number, end: number}>>;
}

const DashboardDataContext = createContext<DashboardDataContextInterface | undefined>(undefined);

// Global sourcesInitialized state to share across all hook instances
const globalSourcesInitialized: { [key: string]: boolean | undefined } = {};

// Global functions to manage sourcesInitialized
export const getGlobalSourcesInitialized = (key: string): boolean | undefined => {
    return globalSourcesInitialized[key];
};

export const setGlobalSourcesInitialized = (key: string, value: boolean | undefined): void => {
    globalSourcesInitialized[key] = value;
};

export const clearGlobalSourcesInitialized = (parameterId: number): void => {
    const allKeys = Object.keys(globalSourcesInitialized);
    const forceClearKeys = [
        `recommended-${parameterId}`,
        ...allKeys.filter(key => key.includes(`source-${parameterId}-`))
    ];
    
    forceClearKeys.forEach(key => {
        globalSourcesInitialized[key] = undefined;
    });
};

export const useDashboardDataContext = () => {
    const context = useContext(DashboardDataContext);
    if (!context) {
        throw new Error('useDashboardDataContext must be used within a DashboardDataProvider');
    }
    return context;
};

export const DashboardDataProvider = ({ children, initialValue = {} }: any) => {
    const [selectedSources, setSelectedSources] = useState<any[]>(initialValue.selectedSources || []);
    const [selectedParamsList, setSelectedParamsList] = useState<any[]>(initialValue.selectedParamsList || []);
    const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>(initialValue.selectedRowKeys || []);
    
    const [sourcesByParam, setSourcesByParam] = useState<any[]>(initialValue.sourcesByParam || []);
    const [showSources, setShowSources] = useState<boolean>(initialValue.showSources || false);
    const [showStrategies, setShowStrategies] = useState<boolean>(initialValue.showStrategies || false);
    const [seriesVisibilityState, setSeriesVisibilityState] = useState<Map<string, boolean>>(new Map());
    const [selectedSourceToAnnotation, setSelectedSourceToAnnotation] = useState<string>('');
    const [selectedQualityAnnotationRange, setSelectedQualityAnnotationRange] = useState<{start: number, end: number}>({start: 0, end: 0});
    // Debouncing mechanism to prevent duplicate calls
    const pendingToggles = useRef<Map<string, NodeJS.Timeout>>(new Map());
    
    // Function to toggle series visibility with debouncing
    const toggleSeriesVisibility = useCallback((record: any) => {
        const recordId = record.parameterId;
        
        // Clear any pending toggle for this parameter
        const existingTimeout = pendingToggles.current.get(recordId.toString());
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }
        
        // Set a new timeout to debounce the toggle
        const newTimeout = setTimeout(() => {
            setSeriesVisibilityState(prevState => {
                const currentVisibility = prevState.get(recordId) ?? true;
                const newVisibility = !currentVisibility;
                
                const newVisibilityState = new Map(prevState);
                newVisibilityState.set(recordId, newVisibility);
                
                return newVisibilityState;
            });
            
            // Remove the timeout from pending toggles
            pendingToggles.current.delete(recordId.toString());
        }, 50); // 50ms debounce
        
        // Store the new timeout
        pendingToggles.current.set(recordId.toString(), newTimeout);
    }, [seriesVisibilityState]);

    const contextValue = useMemo(() => ({
        selectedSources,
        setSelectedSources,
        selectedParamsList,
        setSelectedParamsList,
        selectedRowKeys,
        setSelectedRowKeys,
        
        sourcesByParam,
        setSourcesByParam,
        showSources,
        setShowSources,
        showStrategies,
        setShowStrategies,
        seriesVisibilityState,
        setSeriesVisibilityState,
        toggleSeriesVisibility,
        selectedSourceToAnnotation,
        setSelectedSourceToAnnotation,
        selectedQualityAnnotationRange,
        setSelectedQualityAnnotationRange
    }), [
        selectedSources,
        selectedParamsList,
        selectedRowKeys,
        sourcesByParam,
        showSources,
        showStrategies,
        seriesVisibilityState,
        toggleSeriesVisibility,
        selectedSourceToAnnotation,
        setSelectedSourceToAnnotation,
        selectedQualityAnnotationRange,
        setSelectedQualityAnnotationRange
    ]);

    return (
        <DashboardDataContext.Provider value={contextValue}>
            {children}
        </DashboardDataContext.Provider>
    );
};
